import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { DASH } from "@/constants/ui";
import { toast } from "sonner";
import TeamCalendar from "@/components/TeamCalendar";
import {
  ArrowRight,
  CalendarDays,
  CheckSquare,
  Clock3,
  Users as UsersIcon,
  Play,
  Square,
  AlertCircle,
} from "lucide-react";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function elapsedSince(iso) {
  const start = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - start);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [recentVacations, setRecentVacations] = useState([]);
  const [todos, setTodos] = useState([]);
  const [tick, setTick] = useState(0);

  const reload = async () => {
    try {
      const [s, vRes, tRes] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/vacations", { params: { scope: "mine" } }),
        api.get("/todos", { params: { scope: "mine" } }),
      ]);
      setSummary(s.data);
      setRecentVacations(vRes.data.slice(0, 4));
      setTodos(tRes.data.filter((t) => t.status !== "done").slice(0, 5));
    } catch (e) { void e; }
  };

  useEffect(() => {
    reload();
  }, []);

  useEffect(() => {
    if (!summary?.clocked_in) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [summary?.clocked_in]);

  const clockIn = async () => {
    try {
      await api.post("/time/clock-in");
      toast.success("Eingestempelt");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Einstempeln");
    }
  };
  const clockOut = async () => {
    try {
      await api.post("/time/clock-out");
      toast.success("Ausgestempelt");
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Ausstempeln");
    }
  };

  if (!summary) {
    return (
      <div className="grid md:grid-cols-4 gap-5">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-32 rounded-xl" />
        ))}
      </div>
    );
  }

  const isAdmin = user.role === "admin";

  return (
    <div className="space-y-8">
      {/* Heading */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <p className="ph-label">{new Date().toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Hallo {user.name.split(" ")[0]}.
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            {isAdmin
              ? "Hier ist dein Überblick über das Team."
              : "Schön, dich zu sehen. Hier sind deine Eckdaten."}
          </p>
        </div>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
        <Kpi
          testid={DASH.kpiVacation}
          label="Resturlaub"
          value={`${summary.vacation.remaining}`}
          unit="Tage"
          sub={`${summary.vacation.taken} genommen${summary.vacation.company ? ` · ${summary.vacation.company} Betriebsf.` : ""} · ${summary.vacation.pending} offen`}
          icon={CalendarDays}
          tone="navy"
        />
        <Kpi
          testid={DASH.kpiTodos}
          label="Offene Aufgaben"
          value={`${summary.open_todos}`}
          unit=""
          sub="Persönlich + Team"
          icon={CheckSquare}
          tone="teal"
        />
        {isAdmin ? (
          <Kpi
            testid={DASH.kpiEmployees}
            label="Team"
            value={`${summary.admin.employees}`}
            unit="Mitarbeiter"
            sub={`${summary.admin.pending_vacations} Anträge zu prüfen`}
            icon={UsersIcon}
            tone="sky"
          />
        ) : (
          <Kpi
            label="Mitteilungen"
            value={`${summary.unread_notifications}`}
            unit="ungelesen"
            sub="Antworten zu Anträgen, neue Aufgaben"
            icon={AlertCircle}
            tone="amber"
          />
        )}
      </div>

      {/* Admin admin block */}
      {isAdmin && summary.admin?.pending_vacations > 0 && (
        <Link to="/urlaub" className="block ph-card p-5 md:p-6 group border-kauz-gold/40 bg-gradient-to-r from-kauz-gold/10 to-transparent">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="ph-label text-kauz-gold-dark">Aktion erforderlich</p>
              <p className="font-display text-xl md:text-2xl font-semibold mt-1">
                {summary.admin.pending_vacations} Urlaubsantrag{summary.admin.pending_vacations === 1 ? "" : "e"} warten auf deine Entscheidung
              </p>
            </div>
            <ArrowRight className="h-5 w-5 text-kauz-gold group-hover:translate-x-1 transition" />
          </div>
        </Link>
      )}

      {/* Team calendar */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-semibold">Team-Übersicht</h2>
          <Link to="/urlaub" className="text-xs text-kauz-navy hover:text-kauz-navy-dark font-medium">
            Vollansicht →
          </Link>
        </div>
        <TeamCalendar compact />
      </div>

      {/* Two-col content */}
      <div className="grid lg:grid-cols-3 gap-5">
        {/* Todos */}
        <div className="ph-card p-5 md:p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Aktuelle Aufgaben</h2>
            <Link to="/aufgaben" className="text-xs text-kauz-navy hover:text-kauz-navy-dark font-medium">
              Alle ansehen →
            </Link>
          </div>
          {todos.length === 0 ? (
            <EmptyState text="Keine offenen Aufgaben — ausgezeichnet." />
          ) : (
            <ul className="divide-y divide-slate-100">
              {todos.map((t) => (
                <li key={t.id} className="py-3 flex items-center gap-3">
                  <PriorityDot p={t.priority} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {t.assignee_id ? "Persönlich" : "Team"} {t.due_date && `· fällig ${t.due_date}`}
                    </div>
                  </div>
                  <Badge variant="outline" className="text-[10px] uppercase border-slate-200 text-slate-600">
                    {t.status === "open" ? "Offen" : t.status === "in_progress" ? "Aktiv" : t.status === "review" ? "Prüfung" : "Erledigt"}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Vacations */}
        <div className="ph-card p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-lg font-semibold">Meine Urlaube</h2>
            <Link to="/urlaub" className="text-xs text-kauz-navy hover:text-kauz-navy-dark font-medium">
              Antrag stellen →
            </Link>
          </div>
          {recentVacations.length === 0 ? (
            <EmptyState text="Noch keine Anträge." />
          ) : (
            <ul className="space-y-3">
              {recentVacations.map((v) => (
                <li key={v.id} className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-md bg-slate-50 border border-slate-200 grid place-items-center">
                    <CalendarDays className="h-4 w-4 text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {v.start_date} → {v.end_date}
                    </div>
                    <div className="text-xs text-slate-500">{v.days} Werktage</div>
                  </div>
                  <StatusBadge status={v.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, unit, sub, icon: Icon, tone = "sky", testid }) {
  const tones = {
    sky: "from-sky-50 to-white text-sky-700 border-sky-100",
    teal: "from-teal-50 to-white text-teal-700 border-teal-100",
    emerald: "from-emerald-50 to-white text-emerald-700 border-emerald-100",
    slate: "from-slate-50 to-white text-slate-600 border-slate-200",
    indigo: "from-indigo-50 to-white text-indigo-700 border-indigo-100",
    amber: "from-amber-50 to-white text-amber-700 border-amber-100",
    orange: "from-orange-50 to-white text-orange-700 border-orange-100",
    navy: "from-slate-50 to-white text-kauz-navy border-slate-200",
    gold: "from-amber-50 to-white text-kauz-gold-dark border-amber-100",
    rose: "from-rose-50 to-white text-rose-700 border-rose-100",
  };
  const cls = tones[tone] || tones.sky;
  const iconColor = cls.split(" ").find((c) => c.startsWith("text-")) || "text-slate-600";
  return (
    <div data-testid={testid} className="ph-card p-5 relative overflow-hidden">
      <div className={`absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br ${cls} opacity-70`} />
      <div className="relative">
        <div className="flex items-center justify-between">
          <p className="ph-label">{label}</p>
          <Icon className={`h-4 w-4 ${iconColor}`} />
        </div>
        <div className="mt-3 flex items-end gap-2">
          <span className="ph-kpi text-4xl md:text-5xl text-slate-900">{value}</span>
          {unit && <span className="text-slate-500 text-sm mb-1.5">{unit}</span>}
        </div>
        <p className="text-xs text-slate-500 mt-1">{sub}</p>
      </div>
    </div>
  );
}

function ClockWidget({ summary, onIn, onOut, tick }) {
  const active = summary.clocked_in;
  const since = summary.clock_in_at;
  // tick used to re-render every second
  void tick;
  return (
    <div className="ph-card p-4 md:p-5 flex items-center gap-4 min-w-[280px]">
      <div className={`h-12 w-12 rounded-lg grid place-items-center ${active ? "bg-emerald-50 text-emerald-700" : "bg-slate-50 text-slate-500"}`}>
        <Clock3 className="h-5 w-5" />
      </div>
      <div className="flex-1">
        <p className="ph-label">{active ? "Eingestempelt seit" : "Nicht eingestempelt"}</p>
        <p className="font-display text-xl font-semibold tabular-nums">
          {active ? elapsedSince(since) : "00:00:00"}
        </p>
      </div>
      {active ? (
        <Button data-testid={DASH.clockOut} onClick={onOut} className="bg-slate-900 hover:bg-slate-800 text-white h-10">
          <Square className="h-4 w-4 mr-2" />
          Ausstempeln
        </Button>
      ) : (
        <Button data-testid={DASH.clockIn} onClick={onIn} className="bg-sky-700 hover:bg-sky-800 text-white h-10">
          <Play className="h-4 w-4 mr-2" />
          Einstempeln
        </Button>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: "Offen", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    approved: { label: "Genehmigt", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    rejected: { label: "Abgelehnt", cls: "bg-red-50 text-red-800 border-red-200" },
  };
  const m = map[status] || map.pending;
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md border ${m.cls}`}>
      {m.label}
    </span>
  );
}

function PriorityDot({ p }) {
  const m = { high: "bg-red-500", medium: "bg-amber-500", low: "bg-emerald-500" };
  return <span className={`h-2.5 w-2.5 rounded-full ${m[p] || m.medium}`} />;
}

function EmptyState({ text }) {
  return <div className="text-sm text-slate-400 py-8 text-center">{text}</div>;
}
